import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, MapPin, Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { UsersService } from "../../../services/users.service";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  addressType: "HOME",
  addressLine1: "",
  addressLine2: "",
  landmark: "",
  city: "",
  district: "",
  state: "",
  pincode: "",
  latitude: "",
  longitude: "",
  status: "ACTIVE",
  isActive: true,
  profileCompleted: true,
};

function Field({ label, error, children, required = false }) {
  return (
    <div>
      <label className="mb-2 block text-[12px] font-semibold uppercase tracking-wide text-slate-500">
        {label} {required ? <span className="text-[#d86a78]">*</span> : null}
      </label>
      {children}
      {error ? <div className="mt-1 text-[12px] text-[#d86a78]">{error}</div> : null}
    </div>
  );
}

function baseInputClass(hasError) {
  return `h-[44px] w-full rounded-[12px] border bg-white px-4 text-[14px] text-slate-700 outline-none transition focus:ring-2 ${hasError
      ? "border-[#d86a78] focus:border-[#d86a78] focus:ring-[#f7d7db]"
      : "border-[#f2c6cc] focus:border-[#d86a78] focus:ring-[#f7d7db]"
    }`;
}

function validate(form) {
  const next = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]{10,15}$/;
  const pincodeRegex = /^[0-9]{4,10}$/;
  const numberRegex = /^-?\d+(\.\d+)?$/;

  if (!String(form.name || "").trim()) next.name = "Name is required";
  if (!String(form.email || "").trim()) next.email = "Email is required";
  else if (!emailRegex.test(String(form.email).trim())) next.email = "Enter valid email";

  if (String(form.phone || "").trim() && !phoneRegex.test(String(form.phone).trim())) {
    next.phone = "Phone must be 10 to 15 digits";
  }

  if (String(form.pincode || "").trim() && !pincodeRegex.test(String(form.pincode).trim())) {
    next.pincode = "Enter valid pincode";
  }

  if (String(form.latitude || "").trim() && !numberRegex.test(String(form.latitude).trim())) {
    next.latitude = "Enter valid latitude";
  }

  if (String(form.longitude || "").trim() && !numberRegex.test(String(form.longitude).trim())) {
    next.longitude = "Enter valid longitude";
  }

  return next;
}

export default function UserForm({ mode = "edit" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isView = mode === "view";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [messageModal, setMessageModal] = useState({
    open: false,
    type: "info",
    title: "",
    description: "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const user = await UsersService.getById(id);
        if (!mounted) return;
        setForm({
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          addressType: user.addressType || "HOME",
          addressLine1: user.addressLine1 || "",
          addressLine2: user.addressLine2 || "",
          landmark: user.landmark || "",
          city: user.city || "",
          district: user.district || "",
          state: user.state || "",
          pincode: user.pincode || "",
          latitude: user.latitude || "",
          longitude: user.longitude || "",
          status: user.status || "ACTIVE",
          isActive: Boolean(user.isActive),
          profileCompleted: Boolean(user.profileCompleted),
        });
      } catch (e) {
        if (mounted) {
          setMessageModal({
            open: true,
            type: "error",
            title: "Unable to load user",
            description: e?.message || "Please try again.",
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id]);

  const fullAddress = useMemo(
    () =>
      [form.addressLine1, form.addressLine2, form.landmark, form.city, form.district, form.state, form.pincode]
        .filter(Boolean)
        .join(", "),
    [form]
  );

  const onFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isView) return;

    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    try {
      setSaving(true);
      await UsersService.update(id, {
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim(),
        phone: String(form.phone || "").trim(),
        addressType: form.addressType,
        addressLine1: String(form.addressLine1 || "").trim(),
        addressLine2: String(form.addressLine2 || "").trim(),
        landmark: String(form.landmark || "").trim(),
        city: String(form.city || "").trim(),
        district: String(form.district || "").trim(),
        state: String(form.state || "").trim(),
        pincode: String(form.pincode || "").trim(),
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
        status: form.status,
        isActive: form.status !== "BLOCKED",
        profileCompleted: Boolean(form.profileCompleted),
      });
      setMessageModal({
        open: true,
        type: "success",
        title: "User updated",
        description: "User details have been updated successfully.",
      });
    } catch (err) {
      setMessageModal({
        open: true,
        type: "error",
        title: "Update failed",
        description: err?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const disabled = isView || loading || saving;

  return (
    <div className="w-full">
      <ConfirmModal
        open={messageModal.open}
        variant={messageModal.type === "error" ? "danger" : "info"}
        icon={messageModal.type === "success" ? "success" : "x"}
        title={messageModal.title}
        description={messageModal.description}
        onClose={() => {
          const success = messageModal.title === "User updated";
          setMessageModal({
            open: false,
            type: "info",
            title: "",
            description: "",
          });
          if (success) navigate("/admin/dashboard/users");
        }}
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-[10px] border border-[#f2c6cc] bg-white px-4 py-2 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff8f9]"
        >
          <ArrowLeft size={16} />

        </button>

        <div className="text-left">
          <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
            {isView ? "USER DETAILS" : "EDIT USER"}
          </h1>
          {/* <p className="mt-1 text-[13px] text-slate-500">
            {isView ? "Read user profile and permanent address." : "Update user profile and permanent address."}
          </p> */}
        </div>
      </div>

      {loading ? (
        <div className="rounded-[18px] border border-[#f2c6cc] bg-white px-6 py-12 text-center text-sm text-slate-500">
          Loading user details...
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="rounded-[22px] border border-[#f2c6cc] bg-white p-5 sm:p-6">
             <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
  <div className="flex items-center gap-3">
    <h2 className="text-[18px] font-bold text-slate-700">
      Basic Information
    </h2>

    {isView && (
      <button
        type="button"
        onClick={() => navigate(`/admin/dashboard/users/${id}/edit`)}
        className="inline-flex items-center gap-2 rounded-[10px] border border-[#f2c6cc] bg-white px-4 py-2 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff8f9]"
      >
        <Pencil size={15} />
        Edit Profile
      </button>
    )}
  </div>

  <div
    className={`rounded-full px-3 py-1 text-[12px] font-semibold ${
      form.status === "BLOCKED"
        ? "bg-[#fff1f3] text-[#d86a78]"
        : "bg-[#eef9ea] text-[#59a647]"
    }`}
  >
    {form.status === "BLOCKED" ? "Blocked" : "Active"}
  </div>
</div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Name" error={errors.name} required>
                  <input className={baseInputClass(errors.name)} value={form.name} disabled={disabled} onChange={(e) => onFieldChange("name", e.target.value)} />
                </Field>

                <Field label="Email" error={errors.email} required>
                  <input className={baseInputClass(errors.email)} value={form.email} disabled={disabled} onChange={(e) => onFieldChange("email", e.target.value)} />
                </Field>

                <Field label="Phone" error={errors.phone}>
                  <input className={baseInputClass(errors.phone)} value={form.phone} disabled={disabled} onChange={(e) => onFieldChange("phone", e.target.value)} />
                </Field>

                <Field label="Address Type">
                  <select className={baseInputClass(false)} value={form.addressType} disabled={disabled} onChange={(e) => onFieldChange("addressType", e.target.value)}>
                    <option value="HOME">HOME</option>
                    <option value="WORK">WORK</option>
                    <option value="OTHER">OTHER</option>
                  </select>
                </Field>

                <Field label="Status">
                  <select
                    className={baseInputClass(false)}
                    value={form.status}
                    disabled={disabled}
                    onChange={(e) => onFieldChange("status", e.target.value)}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="BLOCKED">BLOCKED</option>
                  </select>
                </Field>

                <Field label="Profile Completed">
                  <select
                    className={baseInputClass(false)}
                    value={String(form.profileCompleted)}
                    disabled={disabled}
                    onChange={(e) => onFieldChange("profileCompleted", e.target.value === "true")}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </Field>
              </div>
            </div>

            <div className="rounded-[22px] border border-[#f2c6cc] bg-[#fffafb] p-5 sm:p-6">
              <h2 className="text-[18px] font-bold text-slate-700">Address Preview</h2>
              <div className="mt-4 rounded-[18px] border border-[#f2c6cc] bg-white p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-slate-700">Permanent Address</div>
                    <div className="mt-2 text-[13px] leading-6 text-slate-500">{fullAddress || "No address added yet."}</div>
                  </div>
                </div>
              </div>

             <div className="mt-4 grid grid-cols-1 gap-3 text-[13px] text-slate-600 sm:grid-cols-2">
  <div className="min-w-0 rounded-[14px] border border-[#f2c6cc] bg-white px-3 py-3">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
      Latitude
    </div>
    <div
      className="mt-1 max-w-full truncate text-[12px] font-semibold text-slate-700"
      title={String(form.latitude || "-")}
    >
      {form.latitude || "-"}
    </div>
  </div>

  <div className="min-w-0 rounded-[14px] border border-[#f2c6cc] bg-white px-3 py-3">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
      Longitude
    </div>
    <div
      className="mt-1 max-w-full truncate text-[12px] font-semibold text-slate-700"
      title={String(form.longitude || "-")}
    >
      {form.longitude || "-"}
    </div>
  </div>
</div>
            </div>
          </div>

          <div className="rounded-[22px] border border-[#f2c6cc] bg-white p-5 sm:p-6">
            <h2 className="mb-5 text-[18px] font-bold text-slate-700">Permanent Address</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <Field label="Address Line 1">
                <input className={baseInputClass(false)} value={form.addressLine1} disabled={disabled} onChange={(e) => onFieldChange("addressLine1", e.target.value)} />
              </Field>
              <Field label="Address Line 2">
                <input className={baseInputClass(false)} value={form.addressLine2} disabled={disabled} onChange={(e) => onFieldChange("addressLine2", e.target.value)} />
              </Field>
              <Field label="Landmark">
                <input className={baseInputClass(false)} value={form.landmark} disabled={disabled} onChange={(e) => onFieldChange("landmark", e.target.value)} />
              </Field>
              <Field label="City">
                <input className={baseInputClass(false)} value={form.city} disabled={disabled} onChange={(e) => onFieldChange("city", e.target.value)} />
              </Field>
              <Field label="District">
                <input className={baseInputClass(false)} value={form.district} disabled={disabled} onChange={(e) => onFieldChange("district", e.target.value)} />
              </Field>
              <Field label="State">
                <input className={baseInputClass(false)} value={form.state} disabled={disabled} onChange={(e) => onFieldChange("state", e.target.value)} />
              </Field>
              <Field label="Pincode" error={errors.pincode}>
                <input className={baseInputClass(errors.pincode)} value={form.pincode} disabled={disabled} onChange={(e) => onFieldChange("pincode", e.target.value)} />
              </Field>
              <Field label="Latitude" error={errors.latitude}>
                <input className={baseInputClass(errors.latitude)} value={form.latitude} disabled={disabled} onChange={(e) => onFieldChange("latitude", e.target.value)} />
              </Field>
              <Field label="Longitude" error={errors.longitude}>
                <input className={baseInputClass(errors.longitude)} value={form.longitude} disabled={disabled} onChange={(e) => onFieldChange("longitude", e.target.value)} />
              </Field>
            </div>
          </div>

          {!isView ? (
            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard/users")}
                className="h-[44px] rounded-[12px] border border-[#f2c6cc] px-6 text-[13px] font-semibold text-slate-500 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="h-[44px] rounded-[12px] bg-[#d86a78] px-6 text-[13px] font-semibold text-white transition hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? "Saving..." : "Update User"}
              </button>
            </div>
          ) : null}
        </form>
      )}
    </div>
  );
}
